import React, { useEffect, useState } from "react";
import { spotifyApi } from "../spotify/spotifyApi";
import { pausePlayback, resumeCurrentPlayback, nextTrack, previousTrack, seekSpotifyPlayback } from "../spotify/spotifyPlayer";

import marbleTemplate from "../ambient/templates/marble/template";
import marbleBackground from "../ambient/templates/marble/ambient-marble.jpg";

function fmt(ms = 0) {
  const total = Math.floor(ms / 1000);
  const m = Math.floor(total / 60);
  const s = String(total % 60).padStart(2, "0");
  return `${m}:${s}`;
}

const displayFocusSelector = [
  ".display-control",
  ".display-progress-slider",
  ".display-nav-item",
  ".display-queue-track",
].join(",");

const AMBIENT2_TEMPLATE_SCALE = 0.5;

function scaledAmbientZone(zone) {
  return {
    left: Math.round(zone.x * AMBIENT2_TEMPLATE_SCALE),
    top: Math.round(zone.y * AMBIENT2_TEMPLATE_SCALE),
    width: Math.round(zone.width * AMBIENT2_TEMPLATE_SCALE),
    height: Math.round(zone.height * AMBIENT2_TEMPLATE_SCALE),
    rotation: zone.rotation || 0,
  };
}

const AMBIENT2_ALBUM_ZONE = scaledAmbientZone(marbleTemplate.album);
const AMBIENT2_PAPER_TEXT = scaledAmbientZone(marbleTemplate.setlist);
const AMBIENT2_INFO_ZONE = scaledAmbientZone(marbleTemplate.info);
const AMBIENT2_CLOCK_ZONE = scaledAmbientZone(marbleTemplate.clock);
const AMBIENT2_SETLIST_ROW_HEIGHT = 22;

function getYearFromValue(value) {
  if (!value) return "";
  const text = String(value);
  const match = text.match(/\b(19|20)\d{2}\b/);
  if (match?.[0]) return match[0];
  return "";
}

function getNumberFromValue(value) {
  if (value === null || value === undefined || value === "") return 0;
  const direct = Number(value);
  if (Number.isFinite(direct)) return direct;
  const match = String(value).match(/\d+(?:\.\d+)?/);
  return match ? Number(match[0]) : 0;
}

function artistLogoSlug(value = "") {
  return String(value)
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

const ARTIST_LOGO_OFFSETS = {
  "architects": 10,
  "as-i-lay-dying": 12,
  "shinedown": 10,
  "killswitch-engage": 12,
  "ghost": 0,
  "bury-tomorrow": 8,
  "40-below-summer": 12,
  "alexisonfire": 10,
  "eminem": 0,
  "adele": 0,
  "ac-dc": 0,
  "abba": 0,
};

function artistLogoOffset(slug = "") {
  return ARTIST_LOGO_OFFSETS[slug] ?? 0;
}

function getAmbientAlbumYear(pjState, track, queue = []) {
  const firstQueue = queue[0] || {};
  const queueYearItem = queue.find((item) =>
    item?.year ||
    item?.albumYear ||
    item?.parentYear ||
    item?.originalYear ||
    item?.originallyAvailableAt
  );
  const directYear =
    pjState?.year ||
    pjState?.albumYear ||
    pjState?.parentYear ||
    pjState?.originalYear ||
    getYearFromValue(pjState?.originallyAvailableAt) ||
    track?.year ||
    track?.albumYear ||
    track?.parentYear ||
    track?.originalYear ||
    getYearFromValue(track?.originallyAvailableAt) ||
    track?.album?.year ||
    firstQueue?.year ||
    firstQueue?.albumYear ||
    firstQueue?.parentYear ||
    firstQueue?.originalYear ||
    getYearFromValue(firstQueue?.originallyAvailableAt) ||
    queueYearItem?.year ||
    queueYearItem?.albumYear ||
    queueYearItem?.parentYear ||
    queueYearItem?.originalYear ||
    getYearFromValue(queueYearItem?.originallyAvailableAt);
  if (directYear) return String(directYear);

  const albumText = String(pjState?.album || track?.album?.name || "");
  const albumYear = getYearFromValue(albumText);
  if (albumYear) return albumYear;

  // Last resort only: addedAt is library timing, not album release timing.
  const addedAt =
    pjState?.addedAt ||
    track?.addedAt ||
    queue.find((item) => item?.addedAt)?.addedAt;

  if (addedAt) {
    const n = Number(addedAt);
    const date = new Date(n > 10000000000 ? n : n * 1000);
    if (!Number.isNaN(date.getTime())) return String(date.getFullYear());
  }

  return "";
}

function buildAmbient2BackgroundUrl(ratingKey, art) {
  if (!ratingKey && !art) return "";
  const params = new URLSearchParams();
  if (ratingKey) params.set("ratingKey", ratingKey);
  if (art) params.set("art", art);
  return `/api/ambient2-bg?${params.toString()}`;
}

const barDrawerItems = [
  "NOW PLAYING",
  "QUEUE",
  "ARTISTS",
  "ALBUMS",
  "PLAYLISTS",
  "LYRICS",
  "SETTINGS",
  "AMBIENT 2",
  "APPS",
  "HOME",
];

function drawerPageFor(item = "") {
  return String(item).toLowerCase().replace(/\s+/g, "-");
}

function drawerIndexForPage(page = "") {
  const idx = barDrawerItems.findIndex((item) => drawerPageFor(item) === page);
  return idx >= 0 ? idx : 0;
}

const barPageValues = new Set(["apps", "now-playing", "queue", "home", "ambient-2", "artists", "albums", "playlists", "lyrics", "settings"]);

function pageFromBarQuery() {
  const initialBarPage = new URLSearchParams(window.location.search).get("page") || "apps";
  return barPageValues.has(initialBarPage) ? initialBarPage : "apps";
}

function visibleDisplayItems() {
  return Array.from(document.querySelectorAll(displayFocusSelector)).filter((el) => {
    const rect = el.getBoundingClientRect();
    const style = window.getComputedStyle(el);
    return (
      !el.disabled &&
      rect.width > 0 &&
      rect.height > 0 &&
      style.display !== "none" &&
      style.visibility !== "hidden"
    );
  });
}

function focusDisplayItem(target) {
  if (!target) return;

  document
    .querySelectorAll(".napflix-tv-focused")
    .forEach((node) => node.classList.remove("napflix-tv-focused"));

  target.classList.add("napflix-tv-focused");
  target.focus?.({ preventScroll: true });
  window.__napflixCurrentFocus = target;
  target.scrollIntoView?.({ block: "nearest", inline: "nearest" });
}

function displayRows() {
  const rows = [];

  visibleDisplayItems().forEach((el) => {
    const rect = el.getBoundingClientRect();
    const top = rect.top;
    let row = rows.find((candidate) => Math.abs(candidate.top - top) < 42);

    if (!row) {
      row = { top, items: [] };
      rows.push(row);
    }

    row.items.push({ el, rect });
    row.top =
      row.items.reduce((sum, item) => sum + item.rect.top, 0) /
      row.items.length;
  });

  rows.forEach((row) =>
    row.items.sort((a, b) => a.rect.left - b.rect.left)
  );

  return rows.sort((a, b) => a.top - b.top);
}

function nearestDisplayItem(row, x) {
  return row?.items?.reduce((best, item) => {
    const cx = item.rect.left + item.rect.width / 2;
    const distance = Math.abs(cx - x);
    return !best || distance < best.distance ? { item, distance } : best;
  }, null)?.item?.el;
}

function dispatchPJDisplayCommand(type, detail) {
  const payload = {
    id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
    type,
    detail: detail || {},
    createdAt: Date.now(),
  };
  const serialized = JSON.stringify(payload);

  localStorage.setItem("pj_display_command", serialized);

  fetch("/api/display-command", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: serialized,
  }).catch(() => {});

  window.dispatchEvent(
    new StorageEvent("storage", {
      key: "pj_display_command",
      newValue: serialized,
    })
  );
}

export default function DisplayPage({
  source = null,
  onPlay,
  onPause,
  onNext,
  onPrevious,
  onSeek,
  queue = [],
  queueIndex = 0,
  onPlayQueueTrack,
}) {
  const [state, setState] = useState(null);
  const [pjState, setPjState] = useState(null);
  const [position, setPosition] = useState(0);
  const [meterSeed, setMeterSeed] = useState(0);
  const [activePanel, setActivePanel] = useState("now");
  const [rightPanelMode, setRightPanelMode] = useState(() => localStorage.getItem("napflix_right_panel_mode") || "volume");
  const [lyricsData, setLyricsData] = useState(null);
  const [lyricsLoading, setLyricsLoading] = useState(false);
  const [ambientAlbumBg, setAmbientAlbumBg] = useState(null);
  const [clockNow, setClockNow] = useState(() => new Date());
  const [viewportSize, setViewportSize] = useState(() => ({
    width: window.innerWidth || 1280,
    height: window.innerHeight || 400,
  }));
  const [barPage, setBarPage] = useState(() => pageFromBarQuery());
  const [barDrawerOpen, setBarDrawerOpen] = useState(false);
  const [barDrawerIndex, setBarDrawerIndex] = useState(0);
  const [barAppRow, setBarAppRow] = useState(0);
  const [barAppIndex, setBarAppIndex] = useState(0);
  const [ambient2SelectedIndex, setAmbient2SelectedIndex] = useState(0);
  const isBarDisplay =
    new URLSearchParams(window.location.search).get("screen") === "bar" ||
    viewportSize.width / Math.max(1, viewportSize.height) >= 2.8;
  const toggleRightPanelMode = () =>
    setRightPanelMode((mode) => (mode === "volume" ? "lyrics" : "volume"));

  useEffect(() => {
    localStorage.setItem("napflix_right_panel_mode", rightPanelMode);
  }, [rightPanelMode]);

  useEffect(() => {
    const timer = setInterval(() => setClockNow(new Date()), 30000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const onResize = () => {
      setViewportSize({
        width: window.innerWidth || 1280,
        height: window.innerHeight || 400,
      });
    };

    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    if (!isBarDisplay) return;
    setBarPage(pageFromBarQuery());
  }, [isBarDisplay]);

  useEffect(() => {
    const initialFocus = window.setTimeout(() => {
      focusDisplayItem(document.querySelector(".display-play"));
    }, 0);

    const onKey = (event) => {
      const handledKeys = [
        "ArrowLeft",
        "ArrowRight",
        "ArrowUp",
        "ArrowDown",
        "Enter",
        "NumpadEnter",
        "Select",
        "OK",
        " ",
        "Spacebar",
      ];

      if (!handledKeys.includes(event.key)) return;
      if (isBarDisplay) return;

      const rows = displayRows();
      if (!rows.length) return;

      const active =
        document.activeElement?.matches?.(displayFocusSelector)
          ? document.activeElement
          : window.__napflixCurrentFocus?.matches?.(displayFocusSelector)
          ? window.__napflixCurrentFocus
          : document.querySelector(".napflix-tv-focused");

      const activeRowIndex = rows.findIndex((row) =>
        row.items.some((item) => item.el === active)
      );
      const activeRow = rows[activeRowIndex];
      const activeItemIndex =
        activeRow?.items.findIndex((item) => item.el === active) ?? -1;

      if (
        event.key === "Enter" ||
        event.key === "NumpadEnter" ||
        event.key === "Select" ||
        event.key === "OK" ||
        event.key === " " ||
        event.key === "Spacebar"
      ) {
        const activeDisplayItem = [
          document.activeElement,
          window.__napflixCurrentFocus,
          document.querySelector(".napflix-tv-focused"),
        ]
          .map((el) =>
            el?.matches?.(displayFocusSelector)
              ? el
              : el?.closest?.(displayFocusSelector)
          )
          .find(Boolean);

        if (activeDisplayItem?.matches?.(".display-progress-slider")) return;
        event.preventDefault();
        event.stopPropagation();
        console.debug("Display activation:", event.key, activeDisplayItem?.className);
        if (activeDisplayItem?.matches?.(".display-play")) {
          dispatchPJDisplayCommand("pj:displayTogglePlay");
          return;
        }

        if (activeDisplayItem?.matches?.(".display-prev")) {
          dispatchPJDisplayCommand("pj:displayPrevious");
          return;
        }

        if (activeDisplayItem?.matches?.(".display-next")) {
          dispatchPJDisplayCommand("pj:displayNext");
          return;
        }

        const panel = activeDisplayItem?.dataset?.panel;
        if (panel === "now" || panel === "queue") {
          setActivePanel(panel);
          return;
        }

        if (activeDisplayItem?.matches?.(".display-queue-track")) {
          const idx = Number(activeDisplayItem.dataset.queueIndex);
          if (Number.isFinite(idx)) {
            onPlayQueueTrack?.(idx);
            dispatchPJDisplayCommand("pj:displayPlayQueueIndex", { index: idx });
          }
          return;
        }

        return;
      }

      event.preventDefault();

      if (activeRowIndex === -1 || activeItemIndex === -1) {
        focusDisplayItem(document.querySelector(".display-play") || rows[0]?.items[0]?.el);
        return;
      }

      if (event.key === "ArrowRight" && active?.matches?.(".display-settings-nav")) {
        event.preventDefault();
        event.stopPropagation();
        toggleRightPanelMode();
        focusDisplayItem(active);
        return;
      }

      if (event.key === "ArrowRight") {
        focusDisplayItem(activeRow.items[activeItemIndex + 1]?.el);
        return;
      }

      if (event.key === "ArrowLeft") {
        focusDisplayItem(activeRow.items[activeItemIndex - 1]?.el);
        return;
      }

      const activeRect = activeRow.items[activeItemIndex].rect;
      const activeCenterX = activeRect.left + activeRect.width / 2;

      if (event.key === "ArrowDown") {
        focusDisplayItem(nearestDisplayItem(rows[activeRowIndex + 1], activeCenterX));
        return;
      }

      if (event.key === "ArrowUp") {
        focusDisplayItem(nearestDisplayItem(rows[activeRowIndex - 1], activeCenterX));
      }
    };

    window.addEventListener("keydown", onKey);
    return () => {
      window.clearTimeout(initialFocus);
      window.removeEventListener("keydown", onKey);
    };
  }, [isBarDisplay, onPlayQueueTrack]);

  useEffect(() => {
    if (!isBarDisplay) return undefined;

    const onBarKey = (event) => {
      const activationKeys = ["Enter", "NumpadEnter", "Select", "OK", " ", "Spacebar"];
      const handledKeys = ["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown", ...activationKeys];
      if (!handledKeys.includes(event.key)) return;

      if (!barDrawerOpen && barPage === "ambient-2") {
        const ambient2TrackCount = Math.max(
          0,
          pjState?.queue?.length || queue?.length || (pjState?.track ? 1 : 0)
        );
        const ambient2LastIndex = Math.max(0, ambient2TrackCount - 1);

        if (event.key === "ArrowRight") {
          event.preventDefault();
          event.stopPropagation();
          setBarDrawerIndex(drawerIndexForPage(barPage));
          setBarDrawerOpen(true);
          return;
        }

        if (event.key === "ArrowDown") {
          event.preventDefault();
          event.stopPropagation();
          setAmbient2SelectedIndex((index) =>
            Math.min(index + 1, ambient2LastIndex)
          );
          return;
        }

        if (event.key === "ArrowUp") {
          event.preventDefault();
          event.stopPropagation();
          setAmbient2SelectedIndex((index) => Math.max(index - 1, 0));
          return;
        }

        if (activationKeys.includes(event.key)) {
          event.preventDefault();
          event.stopPropagation();

          const idx = Math.min(Math.max(0, Number(ambient2SelectedIndex)), ambient2LastIndex);
          if (!Number.isFinite(idx) || !ambient2TrackCount) return;

          const tracks = pjState?.queue?.length ? pjState.queue : queue || [];
          const selectedTrack = tracks[idx] || {};
          const selectedRatingKey = String(selectedTrack?.ratingKey || "");
          const currentRatingKey = String(pjState?.ratingKey || "");
          const selectedTitle = String(selectedTrack?.title || selectedTrack?.name || "").trim().toLowerCase();
          const currentTitle = String(pjState?.track || "").trim().toLowerCase();

          const isSelectedCurrent =
            (selectedRatingKey && currentRatingKey && selectedRatingKey === currentRatingKey) ||
            (selectedTitle && currentTitle && selectedTitle === currentTitle);

          if (isSelectedCurrent) {
            dispatchPJDisplayCommand(pjState?.playing ? "pj:displayPause" : "pj:displayPlay");
            return;
          }

          onPlayQueueTrack?.(idx);
          dispatchPJDisplayCommand("pj:displayPlayQueueIndex", { index: idx });
          return;
        }
      }

      if (!barDrawerOpen && barPage === "apps") {
        if (event.key === "ArrowRight") {
          event.preventDefault();
          event.stopPropagation();
          setBarAppIndex((index) => Math.min(index + 1, [4, 4, 5][barAppRow] - 1));
          return;
        }

        if (event.key === "ArrowLeft") {
          event.preventDefault();
          event.stopPropagation();
          setBarAppIndex((index) => Math.max(index - 1, 0));
          return;
        }

        if (event.key === "ArrowDown") {
          event.preventDefault();
          event.stopPropagation();
          setBarAppRow((row) => {
            const nextRow = Math.min(row + 1, 2);
            setBarAppIndex((index) => Math.min(index, [4, 4, 5][nextRow] - 1));
            return nextRow;
          });
          return;
        }

        if (event.key === "ArrowUp") {
          event.preventDefault();
          event.stopPropagation();
          setBarAppRow((row) => {
            const nextRow = Math.max(row - 1, 0);
            setBarAppIndex((index) => Math.min(index, [4, 4, 5][nextRow] - 1));
            return nextRow;
          });
          return;
        }

        if (activationKeys.includes(event.key)) {
          event.preventDefault();
          event.stopPropagation();
          return;
        }
      }

      if (event.key === "ArrowRight" && !barDrawerOpen) {
        event.preventDefault();
        event.stopPropagation();
        setBarDrawerIndex(drawerIndexForPage(barPage));
        setBarDrawerOpen(true);
        return;
      }

      if (barDrawerOpen && ["ArrowLeft", "Escape", "Backspace", "Back"].includes(event.key)) {
        event.preventDefault();
        event.stopPropagation();
        setBarDrawerOpen(false);
        return;
      }

      if (!barDrawerOpen) return;

      event.preventDefault();
      event.stopPropagation();

      if (event.key === "ArrowDown") {
        setBarDrawerIndex((index) => (index + 1) % barDrawerItems.length);
        return;
      }

      if (event.key === "ArrowUp") {
        setBarDrawerIndex((index) => (index - 1 + barDrawerItems.length) % barDrawerItems.length);
        return;
      }

      if (activationKeys.includes(event.key)) {
        const item = barDrawerItems[barDrawerIndex] || "APPS";
        setBarPage(drawerPageFor(item));
        setBarDrawerOpen(false);
      }
    };

    window.addEventListener("keydown", onBarKey, true);
    return () => window.removeEventListener("keydown", onBarKey, true);
  }, [ambient2SelectedIndex, barAppRow, barDrawerIndex, barDrawerOpen, barPage, isBarDisplay, onPlayQueueTrack, pjState?.queue?.length, pjState?.track, queue?.length]);

  useEffect(() => {
    const readPJState = async () => {
      try {
        const res = await fetch("/api/display-state", { cache: "no-store" });
        const parsed = await res.json();

        if (parsed?.source === "posterjunkie") {
          setPjState(parsed);
          return;
        }

        setPjState(null);
      } catch {}
    };

    readPJState();
    const localPoll = setInterval(readPJState, 1000);

    return () => clearInterval(localPoll);
  }, []);

  useEffect(() => {
    let alive = true;

    async function load() {
      try {
        const data = await spotifyApi.playbackState();
        if (!alive || !data?.item) return;
        setState(data);
        setPosition(data.progress_ms || 0);
      } catch (e) {
        console.warn("Display playback sync failed:", e.message);
      }
    }

    load();
    const poll = setInterval(load, 2500);

    return () => {
      alive = false;
      clearInterval(poll);
    };
  }, []);

  useEffect(() => {
    if (!state?.is_playing) return;

    const t = setInterval(() => {
      setPosition((p) => Math.min(p + 1000, state?.item?.duration_ms || p));
    }, 1000);

    return () => clearInterval(t);
  }, [state]);

  useEffect(() => {
    if (!state?.is_playing) return;

    const t = setInterval(() => {
      setMeterSeed((v) => v + 0.18);
    }, 120);

    return () => clearInterval(t);
  }, [state?.is_playing]);

  async function refreshSoon() {
    setTimeout(async () => {
      try {
        const data = await spotifyApi.playbackState();
        if (data?.item) {
          setState(data);
          setPosition(data.progress_ms || 0);
        }
      } catch (e) {
        console.warn("Display refresh failed:", e.message);
      }
    }, 500);
  }

  async function togglePlay() {
    try {
      if (usingPJ || source === "posterjunkie") {
        dispatchPJDisplayCommand("pj:displayTogglePlay");
        if (displayPlaying) onPause?.();
        else onPlay?.();
        return;
      }

      if (displayPlaying) {
        await pausePlayback();
      } else {
        await resumeCurrentPlayback();
      }

      refreshSoon();
    } catch (e) {
      console.error("Display play/pause failed:", e);
    }
  }

  async function seekDisplay(e) {
    if (!track?.duration_ms) return;

    const pct = Number(e.target.value);
    const nextPos = Math.floor((pct / 100) * track.duration_ms);

    setPosition(nextPos);

    try {
      if (usingPJ || source === "posterjunkie") {
        onSeek?.(pct);
        return;
      }

      await seekSpotifyPlayback(nextPos);
      refreshSoon();
    } catch (e) {
      console.error("Display seek failed:", e);
    }
  }

  async function goNext() {
    try {
      if (usingPJ || source === "posterjunkie") {
        dispatchPJDisplayCommand("pj:displayNext");
        onNext?.();
        return;
      }

      await nextTrack();
      refreshSoon();
    } catch (e) {
      console.error("Display next failed:", e);
    }
  }

  async function goPrevious() {
    try {
      if (usingPJ || source === "posterjunkie") {
        dispatchPJDisplayCommand("pj:displayPrevious");
        onPrevious?.();
        return;
      }

      await previousTrack();
      refreshSoon();
    } catch (e) {
      console.error("Display previous failed:", e);
    }
  }

  const spotifyTrack = state?.item;
  const pjAge = Date.now() - (pjState?.updatedAt || 0);
  const hasPJTrack =
    pjState?.source === "posterjunkie" &&
    pjState?.track &&
    pjState.track !== "Nothing Playing";
  const usingPJ =
    hasPJTrack &&
    (pjState.playing ? pjAge < 30000 : pjAge < 12 * 60 * 60 * 1000);
  const hasSpotifyTrack = !!spotifyTrack?.name;

  const displayTrack = usingPJ
    ? {
        name: pjState.track,
        artists: [{ name: pjState.artist }],
        album: { name: pjState.album, images: pjState.art ? [{ url: pjState.art }] : [] },
        duration_ms: (pjState.duration || 0) * 1000,
      }
    : hasSpotifyTrack
    ? spotifyTrack
    : null;

  const displayPosition = usingPJ ? (pjState.position || 0) * 1000 : position;
  const displayDuration = displayTrack?.duration_ms || 0;
  const displayPlaying = usingPJ ? !!pjState.playing : !!state?.is_playing;
  const rawSampleRate = getNumberFromValue(pjState?.sampleRate);
  const sampleRateHz = rawSampleRate && rawSampleRate < 1000 ? rawSampleRate * 1000 : rawSampleRate;
  const displaySampleRateKhz = sampleRateHz
    ? Number((sampleRateHz / 1000).toFixed(sampleRateHz % 1000 === 0 ? 0 : 1))
    : null;
  const displayBitDepth = getNumberFromValue(pjState?.bitDepth);
  const displayBitrate = getNumberFromValue(pjState?.bitrate);
  const pjDisplayQuality = [
    pjState?.format || pjState?.codec || "FLAC",
    displayBitDepth ? `${displayBitDepth}-bit` : null,
    displaySampleRateKhz ? `${displaySampleRateKhz} kHz` : null,
    displayBitrate && !displayBitDepth && !displaySampleRateKhz ? `${displayBitrate} kbps` : null,
  ].filter(Boolean).join(" · ");
  const displayQuality = usingPJ
  ? pjDisplayQuality
  : hasSpotifyTrack
  ? "Spotify Premium · High Quality Streaming"
  : "NAPFLIX OS READY";

  useEffect(() => {
    const ratingKey = pjState?.ratingKey;
    if (rightPanelMode !== "lyrics" || !usingPJ || !ratingKey) {
      setLyricsData(null);
      setLyricsLoading(false);
      return undefined;
    }

    let cancelled = false;
    const controller = new AbortController();

    async function loadLyrics() {
      setLyricsLoading(true);
      try {
        const response = await fetch(`/api/lyrics/${encodeURIComponent(ratingKey)}`, {
          signal: controller.signal,
        });
        const data = await response.json();
        if (!cancelled) setLyricsData(data);
      } catch (error) {
        if (!cancelled && error.name !== "AbortError") {
          setLyricsData({ source: "none", synced: false, lines: [], text: "" });
        }
      } finally {
        if (!cancelled) setLyricsLoading(false);
      }
    }

    loadLyrics();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [rightPanelMode, usingPJ, pjState?.ratingKey]);

  useEffect(() => {
    const ratingKey = pjState?.ratingKey;
    const art = pjState?.art;
    if (!isBarDisplay || barPage !== "now-playing" || (!ratingKey && !art)) {
      setAmbientAlbumBg(null);
      return;
    }

    const params = new URLSearchParams();
    if (ratingKey) params.set("ratingKey", ratingKey);
    if (art) params.set("art", art);
    const url = `/api/ambient-album-bg?${params.toString()}`;
    console.log("AMBIENT BG URL", url);
    setAmbientAlbumBg(url);
  }, [isBarDisplay, barPage, pjState?.ratingKey, pjState?.art]);

const isHiResAudio =
  usingPJ &&
  (
    ((displayBitDepth >= 24 || Number(pjState?.bitDepth) >= 24) &&
      (sampleRateHz > 48000 || Number(pjState?.sampleRate) > 48000)) ||
    /24-bit|96\s*kHz|192\s*kHz/i.test(String(displayQuality || pjDisplayQuality || ""))
  );

const track = displayTrack;
  const img = String(track?.album?.images?.[0]?.url || track?.artUrl || track?.art || track?.thumb || pjState?.art || "")
    .replace(/([?&])w=\d+/g, "$1w=1200")
    .replace(/([?&])h=\d+/g, "$1h=1200");
  const trackName = track?.name || track?.title || pjState?.track || "";
  const isIdleDisplay = !usingPJ && !hasSpotifyTrack;
  const idleTime = clockNow.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  const idleDate = clockNow.toLocaleDateString([], { weekday: "long", month: "long", day: "numeric" });
  const pct = displayDuration ? Math.min(100, Math.max(0, (displayPosition / displayDuration) * 100)) : 0;
  const displayQueue = queue?.length ? queue : pjState?.queue || [];
  const displayQueueIndex = queue?.length ? queueIndex : pjState?.queueIndex || 0;
  useEffect(() => {
    if (barPage !== "ambient-2") return;
    setAmbient2SelectedIndex(Math.max(0, Math.min(displayQueueIndex || 0, Math.max(0, displayQueue.length - 1))));
  }, [barPage, displayQueue.length, displayQueueIndex]);
  const syncedLyricLines = lyricsData?.synced ? (lyricsData.lines || []) : [];
  const activeLyricIndex = syncedLyricLines.reduce(
    (activeIndex, line, index) =>
      Number(line?.timeMs || 0) <= displayPosition ? index : activeIndex,
    -1
  );
  const lyricWindowStart = Math.max(0, activeLyricIndex - 3);
  const syncedLyricWindow = syncedLyricLines.slice(
    lyricWindowStart,
    Math.min(syncedLyricLines.length, activeLyricIndex + 5)
  );
  const lyricStackLines = lyricsData?.synced
    ? (lyricsData.lines || []).map((line) => line?.text).filter(Boolean)
    : String(lyricsData?.text || "").split(/\r?\n/).map((line) => line.trim()).filter(Boolean);

  function renderBarDisplay() {
    const barTime = clockNow.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    const hasAmbientTrack = Boolean(
      trackName ||
      track?.album?.name ||
      track?.album ||
      track?.artists?.length ||
      track?.artist ||
      pjState?.track
    );

    const currentTitle = hasAmbientTrack
      ? (trackName || pjState?.track || "")
      : "";

    const currentArtist = hasAmbientTrack
      ? (track?.artists?.map((a) => a.name).join(", ") || track?.artist || pjState?.artist || "")
      : "";

    const currentAlbum = hasAmbientTrack
      ? (track?.album?.name || track?.album || pjState?.album || "")
      : "";

    const currentArtistLogoSlug = artistLogoSlug(currentArtist);
    const currentArtistLogoUrl = currentArtistLogoSlug
      ? `/artist-logos/${currentArtistLogoSlug}.png`
      : "";
    const currentYear = getAmbientAlbumYear(pjState, track, displayQueue);
    if (barPage === "ambient-2" && !currentYear && typeof window !== "undefined" && !window.__ambient2YearDebugLogged) {
      window.__ambient2YearDebugLogged = true;
      console.debug("AMBIENT2 YEAR FIELDS", { pjState, track, firstQueue: displayQueue[0] });
    }
    const barArt = img || "/napflix/napflixos.png";
    const barTitle = (barPage || "apps").replace(/-/g, " ").toUpperCase();
    const appRows = [
      [
        { name: "MUSIC", image: "/appicons/music.png" },
        { name: "MOVIES", image: "/appicons/movies.png" },
        { name: "TV SHOWS", image: "/appicons/tvshows.png" },
        { name: "TV GUIDE", image: "/appicons/tvguide.png" },
      ],
      [
        { name: "SPOTIFY", image: "/appicons/spotify.png" },
        { name: "NAPFLIX", image: "/appicons/napflix.png" },
        { name: "RADIO", image: "/appicons/radio.png" },
        { name: "PLAYLISTS", image: "/appicons/playlists.png" },
      ],
      [
        { name: "FAVORITES", image: "/appicons/favorites.png" },
        { name: "COLLECTIONS", image: "/appicons/collections.png" },
        { name: "AMBIENT", image: "/appicons/ambient.png" },
        { name: "USERS", image: "/appicons/users.png" },
        { name: "SETTINGS", image: "/appicons/settings.png" },
      ],
    ];
    const artistCards = ["Shinedown", "Pearl Jam", "Pink Floyd", "The Beatles", "Radiohead", "Daft Punk", "Miles Davis", "Fleetwood Mac"];
    const activeAppRow = appRows[barAppRow] || appRows[0];
    const appTileSize = activeAppRow.length === 5 ? 216 : 240;
    const ambient2Tracks = displayQueue.length
      ? displayQueue
      : track
      ? [{ title: currentTitle, artist: currentArtist, album: currentAlbum, duration: displayDuration / 1000 }]
      : [];
    const ambient2TrackMatchIndex = ambient2Tracks.findIndex((item) =>
      String(item?.title || item?.name || "") === String(pjState?.track || currentTitle || "")
    );
    const ambient2CurrentIndex = Math.max(
      0,
      Math.min(
        ambient2TrackMatchIndex >= 0 ? ambient2TrackMatchIndex : displayQueueIndex || 0,
        Math.max(0, ambient2Tracks.length - 1)
      )
    );
    const ambient2Selected = Math.max(0, Math.min(ambient2SelectedIndex || 0, Math.max(0, ambient2Tracks.length - 1)));
    const ambient2VisibleTracks = ambient2Tracks;
    const ambient2VisibleRows = 11;
    const ambient2MaxOffset = Math.max(0, ambient2Tracks.length - ambient2VisibleRows);
    const ambient2ScrollOffset = Math.min(
      Math.max(0, ambient2Selected - Math.floor(ambient2VisibleRows / 2)),
      ambient2MaxOffset
    );
    const ambient2Art = pjState?.art || img || "";
    const ambient2Background = buildAmbient2BackgroundUrl(pjState?.ratingKey, ambient2Art);
    const barShell = (content, title = barTitle, backdropOrOptions = null, collapsedHeaderArg = false) => {
      const shellOptions =
        backdropOrOptions && typeof backdropOrOptions === "object" && !Array.isArray(backdropOrOptions)
          ? backdropOrOptions
          : {};
      const backdrop = shellOptions.backdrop || backdropOrOptions;
      const collapsedHeader = !!(shellOptions.collapsedHeader || collapsedHeaderArg);

      return (
      <div
        style={{
          width: "100vw",
          height: "100vh",
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "center",
          overflow: "hidden",
          background: "#000",
        }}
      >
        <div
          style={{
            width: 1280,
            height: 400,
            overflow: "hidden",
            background: backdrop || "linear-gradient(135deg, #050608 0%, #101015 55%, #060607 100%)",
            color: "#fff",
            fontFamily: "Inter, system-ui, sans-serif",
            position: "relative",
          }}
        >
          {!collapsedHeader && (
            <div
              style={{
                height: 48,
                display: "grid",
                gridTemplateColumns: "1fr auto",
                alignItems: "center",
                padding: "0 22px",
                borderBottom: "1px solid rgba(255,255,255,.09)",
                background: "rgba(0,0,0,.28)",
                boxSizing: "border-box",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 14, minWidth: 0 }}>
                <img src="/napflix/napflixos.png" alt="Napflix OS" style={{ height: 24, width: "auto" }} />
                <div style={{ fontSize: 20, letterSpacing: 3, fontWeight: 900, color: "rgba(255,255,255,.9)", whiteSpace: "nowrap" }}>
                  {title}
                </div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 18 }}>
                {usingPJ && (
                  <div style={{ color: "rgba(255,255,255,.52)", fontSize: 11, letterSpacing: 1.8, fontWeight: 900, whiteSpace: "nowrap" }}>
                    {displayQuality}
                  </div>
                )}
                <div style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontSize: 27, color: "#f0b85b", textShadow: "0 0 18px rgba(240,184,91,.25)" }}>
                  {barTime}
                </div>
              </div>
            </div>
          )}
          <div style={{ height: collapsedHeader ? 400 : 352, position: "relative" }}>
            {content}
          </div>
          {barDrawerOpen && (
            <div
              style={{
                position: "absolute",
                top: collapsedHeader ? 0 : 48,
                right: 0,
                bottom: 0,
                width: 292,
                padding: "16px 16px",
                boxSizing: "border-box",
                background: "rgba(5,5,8,.9)",
                borderLeft: "1px solid rgba(255,255,255,.12)",
                boxShadow: "-28px 0 70px rgba(0,0,0,.48)",
                backdropFilter: "blur(18px)",
              }}
            >
              <div style={{ fontSize: 11, letterSpacing: 3, color: "rgba(240,184,91,.76)", fontWeight: 900, marginBottom: 12 }}>
                NAVIGATION
              </div>
              <div style={{ display: "grid", gap: 7 }}>
                {barDrawerItems.map((item, index) => {
                  const selected = index === barDrawerIndex;
                  const active = drawerPageFor(item) === barPage;
                  return (
                    <div
                      key={item}
                      style={{
                        height: 29,
                        borderRadius: 10,
                        display: "flex",
                        alignItems: "center",
                        padding: "0 12px",
                        border: selected ? "1px solid rgba(240,184,91,.9)" : "1px solid rgba(255,255,255,.06)",
                        background: selected ? "rgba(240,184,91,.16)" : active ? "rgba(255,255,255,.07)" : "rgba(255,255,255,.025)",
                        color: selected ? "#fff" : "rgba(255,255,255,.66)",
                        fontSize: 13,
                        letterSpacing: 1.4,
                        fontWeight: 900,
                        boxShadow: selected ? "0 0 24px rgba(240,184,91,.18)" : "none",
                      }}
                    >
                      {item}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
      );
    };
    const nowPlayingContent = (
      <div
        style={{
          height: "100%",
          position: "relative",
          overflow: "hidden",
          padding: 0,
          boxSizing: "border-box",
          backgroundImage: ambientAlbumBg ? `url(${ambientAlbumBg})` : "none",
          backgroundSize: "cover",
          backgroundPosition: "center center",
          backgroundColor: "#020812",
        }}
      >
        <div
          style={{
            position: "absolute",
            inset: 0,
            background: "linear-gradient(90deg, rgba(0,0,0,.32) 0%, rgba(4,22,45,.18) 28%, rgba(0,6,16,.42) 54%, rgba(0,0,0,.9) 100%), linear-gradient(180deg, rgba(0,0,0,.12) 0%, transparent 42%, rgba(0,0,0,.78) 100%)",
            pointerEvents: "none",
          }}
        />
        <div
          style={{
            position: "absolute",
            inset: 0,
            background: "radial-gradient(circle at 30% 42%, rgba(75,142,210,.2) 0%, rgba(27,73,126,.1) 21%, transparent 48%), radial-gradient(circle at 70% 72%, rgba(0,0,0,.62) 0%, transparent 42%)",
            pointerEvents: "none",
          }}
        />
        <div
          style={{
            position: "absolute",
            left: 575,
            top: 78,
            width: 560,
            minWidth: 0,
          }}
        >
          <div style={{ fontSize: 41, lineHeight: 1.05, fontWeight: 650, letterSpacing: 6.8, color: "rgba(255,255,255,.94)", textShadow: "0 8px 38px rgba(0,0,0,.82)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", textTransform: "uppercase" }}>{currentTitle}</div>
          <div style={{ marginTop: 15, fontSize: 19, color: "#4ea4ff", fontWeight: 700, letterSpacing: 7.5, textShadow: "0 0 28px rgba(78,164,255,.35)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", textTransform: "uppercase" }}>{currentArtist}</div>
          <div style={{ marginTop: 12, fontSize: 16, color: "rgba(255,255,255,.52)", fontWeight: 500, letterSpacing: 3.8, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {[currentAlbum, usingPJ && pjState?.year ? pjState.year : null].filter(Boolean).join(" · ")}
          </div>
          <div style={{ marginTop: 44, width: 300, display: "grid", gridTemplateColumns: "1fr", alignItems: "center" }}>
            <div style={{ height: 2, borderRadius: 99, background: "rgba(255,255,255,.13)", overflow: "hidden" }}>
              <div style={{ height: "100%", width: `${pct}%`, background: "linear-gradient(90deg, rgba(78,164,255,.86), rgba(255,255,255,.72))", boxShadow: "0 0 12px rgba(78,164,255,.35)" }} />
            </div>
          </div>
        </div>
        <div style={{ position: "absolute", right: 34, bottom: 20, textAlign: "right", color: "rgba(255,255,255,.34)", fontSize: 18, letterSpacing: 0, fontWeight: 850, fontStyle: "italic", textTransform: "lowercase" }}>
          napflixos
        </div>
      </div>
    );

    const appsContent = (
      <div style={{ height: "100%", padding: "0 38px 8px", boxSizing: "border-box", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>
        <div style={{ width: "100%", height: "100%", display: "flex", justifyContent: "space-evenly", alignItems: "center", gap: activeAppRow.length === 5 ? 24 : 48, overflow: "visible" }}>
            {activeAppRow.map((item, index) => {
              const focused = index === barAppIndex;
              return (
                <div
                  key={item.name}
                  style={{
                    width: appTileSize,
                    height: appTileSize,
                    aspectRatio: "1 / 1",
                    borderRadius: 34,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    transform: focused ? "scale(1.06)" : "scale(1)",
                    transition: "transform 140ms ease, filter 140ms ease",
                    filter: focused ? "drop-shadow(0 0 26px rgba(240,184,91,.6)) drop-shadow(0 0 56px rgba(240,184,91,.26))" : "drop-shadow(0 18px 34px rgba(0,0,0,.38))",
                    boxSizing: "border-box",
                  }}
                >
                  <img
                    src={item.image}
                    alt={item.name}
                    style={{
                      width: "100%",
                      height: "100%",
                      aspectRatio: "1 / 1",
                      objectFit: "cover",
                      display: "block",
                      borderRadius: 32,
                      outline: focused ? "2px solid rgba(240,184,91,.9)" : "1px solid rgba(255,255,255,.08)",
                      outlineOffset: 5,
                    }}
                  />
                </div>
              );
            })}
        </div>
      </div>
    );

    const lyricsContent = (
      <div style={{ height: "100%", display: "grid", gridTemplateColumns: "285px 1fr", gap: 28, alignItems: "center", padding: "18px 30px", boxSizing: "border-box", background: `linear-gradient(90deg, rgba(5,8,20,.88), rgba(15,35,72,.58)), ${img ? `url(${img}) center/cover` : "transparent"}` }}>
        <img src={barArt} alt="" style={{ width: 240, height: 240, objectFit: img ? "cover" : "contain", borderRadius: 22, boxShadow: "0 28px 80px rgba(0,0,0,.55)" }} />
        <div style={{ overflow: "hidden", WebkitMaskImage: "linear-gradient(to bottom, transparent 0%, #000 16%, #000 86%, transparent 100%)", maskImage: "linear-gradient(to bottom, transparent 0%, #000 16%, #000 86%, transparent 100%)" }}>
          {lyricsData?.synced && syncedLyricLines.length ? (
            <div style={{ display: "grid", gap: 14 }}>
              {(syncedLyricWindow.length ? syncedLyricWindow : syncedLyricLines.slice(0, 8)).map((line, idx) => {
                const absoluteIndex = lyricWindowStart + idx;
                const active = absoluteIndex === activeLyricIndex || (activeLyricIndex < 0 && idx === 0);
                return (
                  <div key={`${line.timeMs}-${idx}`} style={{ fontSize: active ? 42 : 30, lineHeight: 1.02, fontWeight: 950, color: active ? "#fff" : "rgba(255,255,255,.35)", textShadow: active ? "0 0 34px rgba(120,180,255,.24)" : "none" }}>
                    {line.text}
                  </div>
                );
              })}
            </div>
          ) : lyricStackLines.length ? (
            <div style={{ display: "grid", gap: 13 }}>
              {lyricStackLines.slice(0, 7).map((line, idx) => (
                <div key={`${line}-${idx}`} style={{ fontSize: idx < 2 ? 38 : 28, lineHeight: 1.05, fontWeight: 950, color: idx < 2 ? "#fff" : "rgba(255,255,255,.38)" }}>{line}</div>
              ))}
            </div>
          ) : (
            <div style={{ fontSize: 34, fontWeight: 950, color: "rgba(255,255,255,.72)" }}>Lyrics unavailable</div>
          )}
        </div>
      </div>
    );

    const artistsContent = (
      <div style={{ height: "100%", padding: "18px 28px", boxSizing: "border-box", display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16 }}>
        {artistCards.map((artist, index) => (
          <div key={artist} style={{ borderRadius: 20, border: index === 0 ? "2px solid rgba(240,184,91,.9)" : "1px solid rgba(255,255,255,.1)", background: "radial-gradient(circle at top, rgba(255,255,255,.1), rgba(255,255,255,.03))", display: "flex", alignItems: "flex-end", padding: 18, boxSizing: "border-box", fontSize: 24, fontWeight: 950, letterSpacing: 1 }}>
            {artist}
          </div>
        ))}
      </div>
    );

    const ambientContent = (
      <div style={{ height: "100%", padding: "22px 34px", boxSizing: "border-box", display: "grid", gridTemplateColumns: "1fr 320px 1fr", gap: 28, alignItems: "center" }}>
        <div>
          <div style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontSize: 96, lineHeight: .9, color: "#f0b85b", fontWeight: 900, textShadow: "0 0 40px rgba(240,184,91,.22)" }}>{barTime}</div>
          <div style={{ marginTop: 12, color: "rgba(196,255,176,.72)", letterSpacing: 2.4, fontWeight: 900 }}>{idleDate.toUpperCase()}</div>
        </div>
        <img src={barArt} alt="" style={{ width: 250, height: 250, objectFit: img ? "cover" : "contain", borderRadius: "50%", border: "1px solid rgba(255,255,255,.14)", boxShadow: "0 0 70px rgba(240,184,91,.12)" }} />
        <div style={{ display: "flex", alignItems: "center", gap: 7, height: 110 }}>
          {Array.from({ length: 24 }).map((_, index) => (
            <div key={index} style={{ width: 7, height: 18 + ((index * 17) % 76), borderRadius: 999, background: "linear-gradient(to top,#f0b85b,#7cff91)", opacity: .3 + (index % 5) * .12 }} />
          ))}
        </div>
      </div>
    );

    const ambient2Content = (
      <div
        style={{
          width: "100%",
          height: "100%",
          position: "relative",
          overflow: "hidden",
          background: "#000",
          backgroundImage: `url(${marbleBackground})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          fontFamily: "\"Oswald\", \"Bebas Neue\", \"Arial Narrow\", sans-serif",
          color: "#241f1a",
        }}
      >
        {/* Napflix logo blended into the tabletop instead of sitting like a sticker. */}
        <img
          src="/napflix/napflixos.png"
          alt="Napflix OS"
          style={{
            position: "absolute",
            left: 26,
            top: 16,
            height: 28,
            width: "auto",
            zIndex: 5,
            opacity: 0.58,
            mixBlendMode: "normal",
            filter: "grayscale(1) brightness(.72) contrast(1.35) drop-shadow(0 1px 1px rgba(255,255,255,.55))",
          }}
        />

        <div
          style={{
            position: "absolute",
            left: AMBIENT2_CLOCK_ZONE.left,
            top: AMBIENT2_CLOCK_ZONE.top,
            width: AMBIENT2_CLOCK_ZONE.width,
            height: AMBIENT2_CLOCK_ZONE.height,
            fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
            color: "rgba(255,255,255,.86)",
            fontSize: 21,
            textShadow: "0 2px 10px rgba(0,0,0,.45)",
            zIndex: 5,
          }}
        >
          {barTime}
        </div>

        {/* Album art locked to the tilted album zone */}
        <div
          style={{
            position: "absolute",
            left: AMBIENT2_ALBUM_ZONE.left + 18,
            top: AMBIENT2_ALBUM_ZONE.top + 18,
            width: AMBIENT2_ALBUM_ZONE.width - 36,
            height: AMBIENT2_ALBUM_ZONE.height - 36,
            transform: `rotate(${AMBIENT2_ALBUM_ZONE.rotation}deg)`,
            transformOrigin: "center center",
            zIndex: 4,
            overflow: "hidden",
            boxShadow: "none",
            background: "transparent",
          }}
        >
          {(pjState?.art || img) ? (
            <img
              src={pjState?.art || img}
              alt=""
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                display: "block",
              }}
            />
          ) : null}
        </div>

        {/* Center metadata, locked between sleeve and paper */}
        <div
          style={{
            position: "absolute",
            left: AMBIENT2_INFO_ZONE.left,
            top: AMBIENT2_INFO_ZONE.top,
            width: AMBIENT2_INFO_ZONE.width,
            height: AMBIENT2_INFO_ZONE.height,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            textAlign: "center",
            color: "#211c18",
            textShadow: "0 1px 0 rgba(255,255,255,.24)",
            zIndex: 4,
          }}
        >
          <div
            style={{
              fontSize: currentAlbum.length > 20 ? 36 : 44,
              lineHeight: .93,
              fontWeight: 950,
              letterSpacing: 1.1,
              textTransform: "uppercase",
            }}
          >
            {currentAlbum === "Front Panel" ? "" : currentAlbum}
          </div>

          <div
            style={{
              marginTop: 14,
              fontSize: 30,
              lineHeight: 1,
              fontWeight: 900,
              letterSpacing: 3.2,
              textTransform: "uppercase",
              color: "#4a4139",
            }}
          >
            {currentArtist === "Ready" ? "" : currentArtist}
          </div>

          <div
            style={{
              marginTop: 9,
              fontSize: 23,
              lineHeight: 1.1,
              fontWeight: 900,
              letterSpacing: 1.4,
              textTransform: "uppercase",
              color: "#625850",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {currentTitle === "Napflix OS" ? "" : currentTitle}
          </div>

          {currentYear && (
            <div
              style={{
                marginTop: 12,
                fontSize: 22,
                lineHeight: 1,
                fontWeight: 900,
                letterSpacing: 3.4,
                color: "#625850",
              }}
            >
              {currentYear}
            </div>
          )}

          <div
            style={{
              marginTop: 24,
              display: "grid",
              gap: 10,
              fontSize: 18,
              letterSpacing: 2.1,
              fontWeight: 900,
              color: "#625850",
            }}
          >
            {isHiResAudio && (
              <div style={{ display: "flex", justifyContent: "center", marginBottom: 6 }}>
                <img
                  src="/napflix/hiresaudio.png"
                  alt="Hi-Res Audio"
                  style={{ height: 48, width: "auto", opacity: .96, filter: "contrast(1.02) saturate(.95)" }}
                />
              </div>
            )}
            {ambient2Tracks.length > 0 && (
              <>
                <div>{pjDisplayQuality || displayQuality}</div>
                <div>{ambient2Tracks.length} TRACKS</div>
              </>
            )}
          </div>
        </div>

        {/* Tracklist locked to the paper area baked into the template */}
        <div
          style={{
            position: "absolute",
            left: AMBIENT2_PAPER_TEXT.left,
            top: AMBIENT2_PAPER_TEXT.top,
            width: AMBIENT2_PAPER_TEXT.width,
            height: AMBIENT2_PAPER_TEXT.height - 34,
            padding: "92px 26px 18px 26px",
            boxSizing: "border-box",
            transform: `rotate(${AMBIENT2_PAPER_TEXT.rotation}deg)`,
            transformOrigin: "center center",
            zIndex: 4,
            color: "#201a15",
            textTransform: "uppercase",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              position: "absolute",
              left: "50%",
              top: 26,
              width: "76%",
              height: 48,
              transform: "translateX(-50%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              overflow: "hidden",
              pointerEvents: "none",
            }}
          >
            {currentArtistLogoUrl ? (
              <>
                <img
                  src={currentArtistLogoUrl}
                  alt=""
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                    const fallback = e.currentTarget.parentElement?.querySelector(".ambient-artist-logo-fallback");
                    if (fallback) fallback.style.display = "block";
                  }}
                  style={{
                    maxWidth: "100%",
                    maxHeight: "100%",
                    width: "auto",
                    height: "auto",
                    objectFit: "contain",
                    objectPosition: "center center",
                    filter: "grayscale(1) contrast(1.55) brightness(.42)",
                    opacity: .95,
                  }}
                />
                <div
                  className="ambient-artist-logo-fallback"
                  style={{
                    display: "none",
                    fontSize: 28,
                    lineHeight: 1,
                    fontWeight: 1000,
                    letterSpacing: -0.8,
                    color: "#111",
                    textTransform: "uppercase",
                    fontFamily: "\"Arial Black\", Impact, \"Arial Narrow\", sans-serif",
                  }}
                >
                  {currentArtist}
                </div>
              </>
            ) : null}
          </div>

          <div
            style={{
              height: "100%",
              overflow: "hidden",
              boxSizing: "border-box",
            }}
          >
            <div
              style={{
                display: "grid",
                transform: `translateY(-${ambient2ScrollOffset * AMBIENT2_SETLIST_ROW_HEIGHT}px)`,
                transition: "transform 180ms ease",
              }}
            >
              {ambient2VisibleTracks.map((q, idx) => {
                const absoluteIndex = idx;
                const selected = absoluteIndex === ambient2Selected;
                const current = absoluteIndex === ambient2CurrentIndex;
                const title = q.title || q.name || "Untitled Track";

                return (
                  <button
                    key={`${q.ratingKey || title}-${absoluteIndex}`}
                    type="button"
                    onClick={() => {
                      setAmbient2SelectedIndex(absoluteIndex);
                      onPlayQueueTrack?.(absoluteIndex);
                      dispatchPJDisplayCommand("pj:displayPlayQueueIndex", { index: absoluteIndex });
                    }}
                    style={{
                      height: AMBIENT2_SETLIST_ROW_HEIGHT,
                      display: "grid",
                      gridTemplateColumns: "18px 1fr",
                      alignItems: "center",
                      gap: 4,
                      border: 0,
                      padding: 0,
                      background: "transparent",
                      color: current ? "#080604" : selected ? "#17110d" : "rgba(32,26,21,.82)",
                      fontFamily: "inherit",
                      fontSize: 13.8,
                      fontWeight: current ? 1000 : selected ? 900 : 820,
                      letterSpacing: 1.1,
                      textAlign: "left",
                      textTransform: "uppercase",
                      cursor: "default",
                      borderBottom: "none",
                      opacity: selected || current ? 1 : .82,
                    }}
                  >
                    <span
                      style={{
                        fontSize: 11,
                        lineHeight: 1,
                        width: 18,
                        textAlign: "center",
                        opacity: current ? 1 : 0,
                      }}
                    >
                      ►
                    </span>

                    <span
                      style={{
                        overflow: "hidden",
                        whiteSpace: "nowrap",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {title}
                    </span>
                  </button>
                );
              })}

              {!ambient2VisibleTracks.length && null}
            </div>
          </div>
        </div>
      </div>
    );

    const settingsContent = (
      <div style={{ height: "100%", padding: "20px 30px", boxSizing: "border-box", display: "grid", gridTemplateColumns: "280px 1fr", gap: 22 }}>
        <div style={{ display: "grid", gap: 10 }}>
          {["AUDIO", "DISPLAY", "SYSTEM", "NETWORK", "REMOTE"].map((item, index) => (
            <div key={item} style={{ borderRadius: 14, border: index === 0 ? "2px solid rgba(240,184,91,.9)" : "1px solid rgba(255,255,255,.1)", padding: "14px 16px", fontWeight: 950, letterSpacing: 2, background: index === 0 ? "rgba(240,184,91,.13)" : "rgba(255,255,255,.04)" }}>{item}</div>
          ))}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16 }}>
          {[["AUDIO", "READY"], ["DISPLAY", "1280 x 400"], ["SYSTEM", "NAPFLIX OS"]].map(([label, value]) => (
            <div key={label} style={{ borderRadius: 20, border: "1px solid rgba(255,255,255,.11)", background: "rgba(255,255,255,.04)", padding: 20 }}>
              <div style={{ fontSize: 12, letterSpacing: 3, color: "rgba(255,255,255,.44)", fontWeight: 900 }}>{label}</div>
              <div style={{ marginTop: 16, fontSize: 28, lineHeight: 1.1, color: "#fff", fontWeight: 950 }}>{value}</div>
            </div>
          ))}
        </div>
      </div>
    );

    if (barPage === "now-playing" || barPage === "queue" || barPage === "home") return barShell(nowPlayingContent, "AMBIENT");
    if (barPage === "lyrics") return barShell(lyricsContent, "LYRICS", "linear-gradient(135deg,#06111f,#0b244a 55%,#030609)");
    if (barPage === "artists" || barPage === "albums" || barPage === "playlists") return barShell(artistsContent, barTitle);
    if (barPage === "ambient") return barShell(ambientContent, "AMBIENT");
    if (barPage === "ambient-2") return barShell(ambient2Content, "", { collapsedHeader: true });
    if (barPage === "settings") return barShell(settingsContent, "SETTINGS");
    return barShell(appsContent, "APPS");
  }

  if (isBarDisplay) {
    return renderBarDisplay();
  }

  return (
    <div style={styles.page}>
      <div style={styles.screen}>
        <div style={styles.top}>
          <div style={styles.brand}>
            <img
              src="/napflix/napflixos.png"
              alt="Napflix OS"
              style={{ height: 30, width: "auto", display: "block" }}
            />
            <span>{isIdleDisplay ? "NAPFLIX OS" : usingPJ ? "HI-RES AUDIO" : "SPOTIFY"}</span>
          </div>
          <div style={styles.meta}>{isIdleDisplay ? "NAPFLIX OS READY" : displayQuality}</div>
        </div>

        <div style={styles.main}>
          {isIdleDisplay ? (
            <div style={styles.idlePanel}>
              <div style={styles.idleGlass}>
                <img
                  src="/napflix/napflixos.png"
                  alt="Napflix OS"
                  style={{ height: 34, width: "auto", opacity: 0.86 }}
                />
                <div style={styles.idleClock}>{idleTime}</div>
                <div style={styles.idleDate}>{idleDate}</div>
                <div style={styles.idleStatus}>
                  <span>READY</span>
                  <span>NO TRACK PLAYING</span>
                </div>
              </div>
            </div>
          ) : (
            <>
              <div style={styles.artBox}>
                {img ? <img src={img} alt="" style={styles.art} /> : <div style={styles.noArt}>
                  <div style={styles.noArtRing}>♪</div>
                </div>}
              </div>

              <div style={styles.info}>
                <div style={styles.title}>{trackName}</div>
                <div style={styles.artist}>{track?.artists?.map(a => a.name).join(", ") || "Napflix OS Player"}</div>
                <div style={styles.album}>{track?.album?.name || ""}</div>

                <div style={styles.format}>
                  {usingPJ
                    ? displayQuality
                    : "Spotify Premium · High Quality Streaming"}
                </div>

                {isHiResAudio ? (
                  <div style={styles.pcm}>
                    <img
                      src="/napflix/hiresaudio.png"
                      alt="Hi-Res Audio"
                      style={{
                        width: 100,
                        maxWidth: "100%",
                        height: "auto",
                        display: "block",
                      }}
                    />
                  </div>
                ) : (
                  <div style={styles.pcm}>
                    {usingPJ ? (
                      <>◉ LOSSLESS&nbsp;&nbsp;{displayQuality}</>
                    ) : (
                      <>◉ STREAM&nbsp;&nbsp;Spotify</>
                    )}
                  </div>
                )}

                <div style={styles.controls}>
                  <button className="display-control display-prev" tabIndex={0} style={styles.controlButton} onClick={goPrevious}>⏮</button>
                  <button className="display-control display-play" tabIndex={0} style={styles.playButton} onClick={togglePlay}>{displayPlaying ? "❚❚" : "▶"}</button>
                  <button className="display-control display-next" tabIndex={0} style={styles.controlButton} onClick={goNext}>⏭</button>
                </div>
                <div style={styles.progressRow}>
                  <span>{fmt(displayPosition)}</span>
                  <div style={styles.progressWrap}>
                    <div style={styles.progress}>
                      <div style={{ ...styles.fill, width: `${pct}%` }} />
                    </div>

                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={Math.round(pct)}
                      onChange={seekDisplay}
                      className="display-progress-slider" tabIndex={0} style={styles.progressSlider}
                    />
                  </div>
                  <span>{fmt(displayDuration)}</span>
                </div>
              </div>
            </>
          )}

          <div style={styles.side}>
            {isIdleDisplay ? (
              <div
                style={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  gap: 22,
                  paddingRight: 18,
                }}
              >
                {[
                  ["SYSTEM", "ONLINE"],
                  ["AUDIO", "READY"],
                  ["LIBRARY", "STANDBY"],
                ].map(([label, value]) => (
                  <div
                    key={label}
                    style={{
                      padding: "18px 20px",
                      borderRadius: 18,
                      border: "1px solid rgba(255,255,255,.1)",
                      background: "linear-gradient(180deg, rgba(255,255,255,.055), rgba(255,255,255,.02))",
                      boxShadow: "inset 0 1px 0 rgba(255,255,255,.06)",
                    }}
                  >
                    <div style={{ fontSize: 12, letterSpacing: 3, color: "rgba(255,255,255,.42)", fontWeight: 900 }}>
                      {label}
                    </div>
                    <div style={{ marginTop: 8, fontSize: 28, letterSpacing: 2, color: "rgba(196,255,176,.82)", fontWeight: 900, textShadow: "0 0 18px rgba(116,255,142,.16)" }}>
                      {value}
                    </div>
                  </div>
                ))}
              </div>
            ) : rightPanelMode === "volume" ? (
              <>
                <div style={styles.volLabel}>VOLUME</div>
                <div style={styles.db}>-18.0 <span>dB</span></div>
                <div style={styles.knob}><div style={styles.dot} /></div>
                <div style={styles.meters}>
                  {Array.from({ length: 22 }).map((_, i) => {
                    const active = displayPlaying;

                    const wave =
                      Math.sin(meterSeed + i * 0.42) * 0.5 +
                      Math.sin(meterSeed * 0.72 + i * 0.18) * 0.5;

                    const normalized = (wave + 1) / 2;

                    const height = active
                      ? 18 + normalized * 28
                      : 16;

                    const opacity = active
                      ? 0.55 + normalized * 0.35
                      : 0.18;

                    return (
                      <div
                        key={i}
                        style={{
                          ...styles.meter,
                          height,
                          opacity,
                        }}
                      />
                    );
                  })}
                </div>
              </>
            ) : (
              <div
                style={{
                  minHeight: "100%",
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  paddingRight: 22,
                }}
              >
                <div
                  style={{
                    width: "100%",
                    minHeight: 430,
                    maxHeight: "calc(100vh - 230px)",
                    overflow: "hidden",
                    position: "relative",
                    WebkitMaskImage: "linear-gradient(to bottom, transparent 0%, #000 12%, #000 82%, transparent 100%)",
                    maskImage: "linear-gradient(to bottom, transparent 0%, #000 12%, #000 82%, transparent 100%)",
                  }}
                >
                  {lyricsLoading ? (
                    <div style={{ minHeight: 430, display: "flex", flexDirection: "column", justifyContent: "center" }}>
                      <div style={{ fontSize: 30, lineHeight: 1.08, fontWeight: 900, color: "rgba(255,255,255,.9)" }}>
                        Loading lyrics
                      </div>
                      <div style={{ marginTop: 14, fontSize: 18, lineHeight: 1.4, fontWeight: 700, color: "rgba(255,255,255,.42)" }}>
                        Checking embedded tags and sidecar files
                      </div>
                    </div>
                  ) : lyricsData?.synced ? (
                    <div>
                      <div style={{ display: "grid", gap: 15, paddingTop: 10 }}>
                        {(syncedLyricWindow.length ? syncedLyricWindow : syncedLyricLines.slice(0, 8)).map((line, idx) => {
                          const absoluteIndex = lyricWindowStart + idx;
                          const distance = Math.abs(absoluteIndex - activeLyricIndex);
                          const isActive = absoluteIndex === activeLyricIndex || (activeLyricIndex < 0 && idx === 0);
                          const opacity = isActive ? 0.96 : Math.max(0.22, 0.58 - distance * 0.1);
                          return (
                          <div
                            key={`${line.timeMs}-${idx}`}
                            style={{
                              fontSize: isActive ? 34 : distance <= 1 ? 28 : 24,
                              lineHeight: 1.08,
                              fontWeight: 900,
                              color: `rgba(255,255,255,${opacity})`,
                              textShadow: isActive ? "0 0 34px rgba(255,255,255,.2)" : "none",
                              transform: isActive ? "scale(1.01)" : "scale(1)",
                              transformOrigin: "left center",
                              transition: "color 220ms ease, font-size 220ms ease, transform 220ms ease",
                            }}
                          >
                            {line.text}
                          </div>
                          );
                        })}
                      </div>
                    </div>
                  ) : lyricsData?.source === "embedded-unsynced" && lyricsData?.text ? (
                    <div style={{ display: "grid", gap: 16, paddingTop: 8 }}>
                      {lyricStackLines.slice(0, 10).map((line, idx) => (
                        <div
                          key={`${line}-${idx}`}
                          style={{
                            fontSize: idx < 2 ? 29 : 25,
                            lineHeight: 1.08,
                            fontWeight: 900,
                            color: idx < 2 ? "rgba(255,255,255,.9)" : "rgba(255,255,255,.38)",
                            textShadow: idx < 2 ? "0 0 30px rgba(255,255,255,.12)" : "none",
                          }}
                        >
                          {line}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={{ minHeight: 430, display: "flex", alignItems: "center" }}>
                      <div style={{ fontSize: 30, lineHeight: 1.08, fontWeight: 900, color: "rgba(255,255,255,.9)" }}>
                        Lyrics unavailable
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {activePanel === "queue" && (
          <div style={styles.queuePanel}>
            <div style={styles.queueHeader}>
              <div>
                <div style={styles.queueEyebrow}>UP NEXT</div>
                <div style={styles.queueTitle}>Current Queue</div>
              </div>
              <div style={styles.queueCount}>{displayQueue.length || 0} tracks</div>
            </div>

            <div style={styles.queueList}>
              {(displayQueue || []).map((q, idx) => (
                <button
                  key={`${q.ratingKey || q.title || "track"}-${idx}`}
                  className="display-queue-track"
                  data-queue-index={idx}
                  tabIndex={0}
                  style={{
                    ...styles.queueTrack,
                    ...(idx === displayQueueIndex ? styles.queueTrackActive : {}),
                  }}
                  onClick={() => {
                    onPlayQueueTrack?.(idx);
                    dispatchPJDisplayCommand("pj:displayPlayQueueIndex", { index: idx });
                  }}
                >
                  <div style={styles.queueNumber}>{idx + 1}</div>
                  <div style={styles.queueText}>
                    <div style={styles.queueTrackTitle}>{q.title || q.name || "Untitled Track"}</div>
                    <div style={styles.queueTrackMeta}>{q.artist || q.albumArtist || q.album || ""}</div>
                  </div>
                  <div style={styles.queueDuration}>{q.duration ? fmt(q.duration * 1000) : ""}</div>
                </button>
              ))}

              {!displayQueue?.length && (
                <div style={styles.queueEmpty}>No queue loaded yet.</div>
              )}
            </div>
          </div>
        )}

        <div style={styles.nav}>
          <button className="display-nav-item" data-panel="now" tabIndex={0} style={{...styles.navButton, ...(activePanel === "now" ? styles.active : {})}} onClick={() => setActivePanel("now")}>▥ NOW PLAYING</button>
          <button className="display-nav-item" data-panel="queue" tabIndex={0} style={{...styles.navButton, ...(activePanel === "queue" ? styles.active : {})}} onClick={() => setActivePanel("queue")}>☰ QUEUE</button>
          <button className="display-nav-item" tabIndex={0} style={styles.navButton}>♬ PLAYLISTS</button>
          <button className="display-nav-item" tabIndex={0} style={styles.navButton}>◎ ALBUMS</button>
          <button className="display-nav-item" tabIndex={0} style={styles.navButton}>⌕ SEARCH</button>
          <button className="display-nav-item display-settings-nav" tabIndex={0} style={styles.navButton}>⚙ SETTINGS</button>
        </div>
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: "100vh",
    background: "#020203",
    display: "grid",
    placeItems: "center",
    color: "#fff",
    fontFamily: "Inter, system-ui, sans-serif",
  },
  screen: {
    width: "100%",
    height: "100%",
    background: `
radial-gradient(circle at top left, rgba(255,180,80,.08), transparent 30%),
radial-gradient(circle at top right, rgba(80,180,255,.08), transparent 30%),
linear-gradient(180deg,#0a0a0b,#020202)
`,
    overflow: "hidden",
  },
  top: {
    height: 70,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "0 44px",
    borderBottom: "1px solid rgba(255,255,255,.08)",
  },
  brand: { display: "flex", alignItems: "center", gap: 16, letterSpacing: 4, fontWeight: 900, fontSize: 15 },
  meta: { color: "rgba(255,255,255,.6)", fontSize: 15 },
  main: {
    height: "calc(100vh - 150px)",
    alignItems: "center",
    display: "grid",
    gridTemplateColumns: "38% 39% 23%",
    padding: "34px 44px",
    boxSizing: "border-box",
  },
  idlePanel: {
    gridColumn: "1 / span 2",
    height: "100%",
    display: "grid",
    placeItems: "center",
    paddingRight: 34,
  },
  idleGlass: {
    width: "min(840px, 94%)",
    minHeight: 430,
    borderRadius: 30,
    border: "1px solid rgba(255,255,255,.12)",
    background: `
linear-gradient(180deg, rgba(255,255,255,.07), rgba(255,255,255,.025)),
radial-gradient(circle at top, rgba(213,168,95,.12), transparent 42%),
rgba(4,5,5,.74)
`,
    boxShadow: "0 32px 110px rgba(0,0,0,.58), inset 0 1px 0 rgba(255,255,255,.08)",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 20,
    backdropFilter: "blur(20px)",
  },
  idleClock: {
    marginTop: 12,
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
    fontSize: "clamp(104px, 13vw, 176px)",
    lineHeight: 0.86,
    fontWeight: 800,
    letterSpacing: 4,
    color: "#f0b85b",
    textShadow: "0 0 22px rgba(240,184,91,.38), 0 0 58px rgba(240,184,91,.2)",
  },
  idleDate: {
    fontSize: 20,
    letterSpacing: 2.4,
    textTransform: "uppercase",
    color: "rgba(196,255,176,.78)",
    textShadow: "0 0 18px rgba(116,255,142,.12)",
  },
  idleStatus: {
    marginTop: 10,
    display: "flex",
    gap: 18,
    alignItems: "center",
    justifyContent: "center",
    color: "rgba(255,255,255,.5)",
    fontSize: 13,
    fontWeight: 900,
    letterSpacing: 2.8,
  },
  artBox: { display: "grid", placeItems: "center" },
  art: { width: "88%", aspectRatio: "1/1", objectFit: "cover", borderRadius: 14 },
  noArt: {
    width: "88%",
    aspectRatio: "1/1",
    borderRadius: 14,
    background: "radial-gradient(circle at center, rgba(213,168,95,.16), rgba(255,255,255,.04) 42%, rgba(0,0,0,.18))",
    display: "grid",
    placeItems: "center",
    boxShadow: "inset 0 0 80px rgba(255,255,255,.04)",
  },
  noArtRing: {
    width: 150,
    height: 150,
    borderRadius: "50%",
    border: "1px solid rgba(213,168,95,.38)",
    display: "grid",
    placeItems: "center",
    fontSize: 72,
    color: "rgba(213,168,95,.76)",
    boxShadow: "0 0 42px rgba(213,168,95,.16)",
  },
  info: { padding: "32px 38px 0 20px" },
  title: { fontSize: "clamp(36px, 4vw, 58px)", fontWeight: 900, lineHeight: 1.02, letterSpacing: "-0.04em" },
  artist: { marginTop: 14, fontSize: 30, color: "rgba(255,255,255,.72)" },
  album: { marginTop: 8, fontSize: 22, color: "rgba(255,255,255,.5)" },
  format: { marginTop: 34, fontSize: 16, color: "rgba(255,255,255,.56)" },
  pcm: { marginTop: 30, fontSize: 42, fontWeight: 800 },
  controls: {
    marginTop: 34,
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    gap: 28,
  },
  controlButton: {
    width: 72,
    height: 72,
    minWidth: 72,
    minHeight: 72,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    border: 0,
    borderRadius: "50%",
    background: "rgba(255,255,255,.04)",
    color: "#fff",
    fontSize: 30,
    cursor: "pointer",
    opacity: 0.86,
  },
  playButton: {
    width: 68,
    height: 68,
    borderRadius: "50%",
    border: "2px solid #d5a85f",
    background: "rgba(255,255,255,.04)",
    color: "#fff",
    fontSize: 24,
    cursor: "pointer",
  },
  progressRow: { marginTop: 32, display: "grid", gridTemplateColumns: "54px 1fr 54px", gap: 14, alignItems: "center" },
  progressWrap: {
    position: "relative",
    height: 18,
    display: "flex",
    alignItems: "center",
  },
  progress: {
    position: "absolute",
    left: 0,
    right: 0,
    top: "50%",
    transform: "translateY(-50%)",
    height: 7,
    borderRadius: 999,
    background: "rgba(255,255,255,.16)",
    overflow: "hidden",
  },
  progressSlider: {
    position: "absolute",
    inset: 0,
    width: "100%",
    opacity: 0.01,
    cursor: "pointer",
  },
  fill: { height: "100%", background: "linear-gradient(90deg,#f5d08a,#d5a85f)", boxShadow: "0 0 18px rgba(213,168,95,.7)" },
  side: { borderLeft: "1px solid rgba(255,255,255,.1)", padding: "32px 0 0 40px" },
  volLabel: { opacity: .62, fontSize: 15 },
  db: { marginTop: 12, fontSize: 48, fontWeight: 300 },
  knob: { marginTop: 20, width: 172, height: 172, borderRadius: "50%", border: "2px solid rgba(255,255,255,.45)", background: `
radial-gradient(circle at 35% 30%, rgba(255,255,255,.18), transparent 18%),
radial-gradient(circle at center, #202020, #050505 68%, #2c2c2c),
linear-gradient(145deg,#111,#000)
`, position: "relative" },
  dot: { position: "absolute", top: 46, right: 42, width: 9, height: 9, borderRadius: "50%", background: "#ddd" },
  meters: {
    marginTop: 34,
    display: "flex",
    gap: 5,
    alignItems: "center",
    height: 58,
    padding: "0 2px",
  },
  meter: {
    width: 7,
    minHeight: 12,
    borderRadius: 999,
    background: `linear-gradient(
      to top,
      rgba(213,168,95,.95),
      rgba(123,255,123,.95)
    )`,
    transition: "height 220ms ease, opacity 220ms ease",
    boxShadow: "0 0 8px rgba(123,255,123,.18)",
  },
  queuePanel: {
    position: "absolute",
    left: 44,
    right: 44,
    bottom: 92,
    maxHeight: "42vh",
    padding: "22px",
    borderRadius: 22,
    background: "rgba(5,5,7,.92)",
    border: "1px solid rgba(255,255,255,.12)",
    boxShadow: "0 24px 80px rgba(0,0,0,.55)",
    backdropFilter: "blur(18px)",
    zIndex: 20,
  },
  queueHeader: {
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "space-between",
    marginBottom: 16,
  },
  queueEyebrow: {
    fontSize: 12,
    letterSpacing: 3,
    color: "rgba(213,168,95,.9)",
    fontWeight: 900,
  },
  queueTitle: {
    marginTop: 4,
    fontSize: 24,
    fontWeight: 900,
  },
  queueCount: {
    color: "rgba(255,255,255,.55)",
    fontSize: 14,
  },
  queueList: {
    display: "grid",
    gap: 8,
    overflowY: "auto",
    maxHeight: "30vh",
    paddingRight: 6,
  },
  queueTrack: {
    width: "100%",
    minHeight: 58,
    display: "grid",
    gridTemplateColumns: "42px 1fr 70px",
    alignItems: "center",
    gap: 12,
    textAlign: "left",
    border: "1px solid rgba(255,255,255,.08)",
    borderRadius: 14,
    background: "rgba(255,255,255,.035)",
    color: "#fff",
    cursor: "pointer",
    padding: "8px 14px",
    font: "inherit",
  },
  queueTrackActive: {
    borderColor: "rgba(213,168,95,.65)",
    background: "rgba(213,168,95,.14)",
  },
  queueNumber: {
    color: "rgba(255,255,255,.45)",
    fontWeight: 800,
  },
  queueText: {
    minWidth: 0,
  },
  queueTrackTitle: {
    fontWeight: 800,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  queueTrackMeta: {
    marginTop: 3,
    color: "rgba(255,255,255,.52)",
    fontSize: 13,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  queueDuration: {
    color: "rgba(255,255,255,.55)",
    textAlign: "right",
    fontSize: 13,
  },
  queueEmpty: {
    padding: 20,
    color: "rgba(255,255,255,.55)",
  },
  nav: { height: 80, borderTop: "1px solid rgba(255,255,255,.1)", display: "grid", gridTemplateColumns: "repeat(6,1fr)", alignItems: "center", textAlign: "center", color: "rgba(255,255,255,.55)" },
  navButton: { height: "100%", border: 0, background: "transparent", color: "rgba(255,255,255,.55)", font: "inherit", cursor: "pointer" },
  active: { color: "#d5a85f" },
};
